// mailArchiveRetry.ts — T-240: Archivieren wartet auf die laufende Mail-Sicherung.
//
// Der Server verschiebt eine VERKNÜPFTE Mail erst, wenn ihre .eml gesichert ist
// (MailMutationGuard, 409 MAIL_CAPTURE_PENDING). Beim „Zuweisen, dann
// archivieren" läuft die Sicherung gerade erst an. Der Server wartet selbst bis
// zu ~6 s; dauert sie länger (große Anhänge) oder ist das Postfach kurz belegt
// (409 MAIL_SOURCE_BUSY), wiederholt der Client hier mit kurzen Requests —
// jeder einzelne bleibt weit unter dem 30-s-Proxy.
import { extractMailServerCode } from "./mailError";

/** Diese 409-Codes sind vorübergehend; alles andere wird sofort durchgereicht. */
export const WARTBARE_ARCHIV_CODES: ReadonlySet<string> = new Set(["MAIL_CAPTURE_PENDING", "MAIL_SOURCE_BUSY"]);

/** Wartezeiten zwischen den Versuchen — zusammen gut 25 s, deckt den Minuten-Cron meist ab. */
export const ARCHIV_WARTESTAFFEL_MS: readonly number[] = [1500, 3000, 5000, 7000, 9000];

export type ArchivWartenDeps = {
    warte?: (ms: number) => Promise<void>;
    staffel?: readonly number[];
    /** Einmal beim ersten Warten — für einen Hinweis „Mail wird gesichert …". */
    onWarten?: () => void;
};

export async function mitSicherungsWarten<T>(versuch: () => Promise<T>, deps: ArchivWartenDeps = {}): Promise<T> {
    const warte = deps.warte ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    const staffel = deps.staffel ?? ARCHIV_WARTESTAFFEL_MS;
    for (let i = 0; ; i++) {
        try {
            return await versuch();
        } catch (err) {
            const code = extractMailServerCode(err);
            if (!code || !WARTBARE_ARCHIV_CODES.has(code) || i >= staffel.length) throw err;
            if (i === 0) deps.onWarten?.();
            await warte(staffel[i]);
        }
    }
}

/**
 * Hat nur der Browser abgebrochen (Zeitlimit)? Dann kann der Server die Mail
 * trotzdem verschoben haben — er arbeitet weiter, auch wenn niemand mehr
 * zuhört.
 */
export function istZeitueberschreitung(err: unknown): boolean {
    return (err as { name?: unknown } | null)?.name === "AbortError";
}

/**
 * Archivieren, das eine Zeitüberschreitung des Browsers übersteht.
 *
 * PROD 26.10.2026: Bei langsamem Graph brach der Browser ab, während der
 * Server die Mail sicherte und verschob. Der nächste Versuch fand sie unter
 * der alten Id nicht mehr (404), und gemeldet wurde „nicht archiviert" — für
 * eine Mail, die im Archiv lag (so auch T-237). Nach einer Zeitüberschreitung
 * wird deshalb einmal nachgefragt: 404 unter der alten Id heißt „schon
 * verschoben" und gilt als Erfolg.
 */
export async function archivierenTrotzZeitlimit<T>(
    versuch: () => Promise<T>,
    schonVerschoben: T,
    istNichtGefunden: (err: unknown) => boolean,
): Promise<T> {
    try {
        return await versuch();
    } catch (err) {
        if (!istZeitueberschreitung(err)) throw err;
    }
    try {
        return await versuch();
    } catch (err) {
        if (istNichtGefunden(err)) return schonVerschoben;
        throw err;
    }
}

/** Ein Ergebnis von `POST /mail/links/batch` — soweit es das Nachholen braucht. */
export type BatchArchivErgebnis = {
    messageId: string;
    archived?: boolean;
    archiveError?: boolean;
    archiveCode?: string | null;
};

/**
 * T-187: Beim Verknüpfen verschiebt der Server die Posteingang-Mails des
 * Verlaufs sofort — ohne auf ihre Sicherung zu warten, die da gerade erst
 * anläuft. Der Guard lehnt dann ab (409 MAIL_CAPTURE_PENDING), und die Mail
 * blieb mit „zugeordnet, aber nicht archiviert (bereits verschoben?)" im
 * Posteingang liegen. Genau diese Mails holt der Client hier über den
 * Archivieren-Weg nach, der auf die Sicherung wartet.
 *
 * Nacheinander, nicht parallel: Im selben Postfach liefen sie sonst in
 * MAIL_SOURCE_BUSY. Ein Ergebnis wird an Ort und Stelle auf „archiviert"
 * gesetzt; scheitert auch das Nachholen, bleibt es als Archivfehler stehen.
 */
export async function archiviereAbgewieseneNach(
    ergebnisse: BatchArchivErgebnis[],
    items: readonly { messageId: string; mailbox: string }[],
    archivieren: (mailbox: string, messageId: string) => Promise<unknown>,
): Promise<void> {
    for (const r of ergebnisse) {
        if (!r.archiveError || !r.archiveCode || !WARTBARE_ARCHIV_CODES.has(r.archiveCode)) continue;
        const item = items.find((i) => i.messageId === r.messageId);
        if (!item) continue;
        try {
            await archivieren(item.mailbox, r.messageId);
            r.archived = true;
            r.archiveError = false;
        } catch {
            // bleibt als Archivfehler gemeldet
        }
    }
}
